import { Routes } from '@angular/router';
import { loginGuard } from './core/guards/login.guard';
import { HomePageComponent } from 'src/app/components/home-page/home-page.component';
import { LoginComponent } from 'src/app/components/login/login.component';
import { ResetPasswordComponent } from 'src/app/components/reset-password/reset-password.component';
import { EmailSentComponent } from 'src/app/components/email-sent/email-sent.component';
import { NewPasswordComponent } from 'src/app/components/new-password/new-password.component';
import { MainLayoutComponent } from 'src/app/components/main-layout/main-layout.component';
import { authGuard } from './core/guards/auth.guard';
import { AccountComponent } from 'src/app/components/account/account.component';

export const APP_ROUTES = {
  login: 'login',
  account: 'account',
  newPassword: 'new-password',
  resetPassword: 'reset-password',
  emailSent: 'email-sent',
  componentsOverview: 'components-overview',
  dashboard: 'dashboard',
  create: 'create',
  edit: 'edit',
  chart: 'chart',
  list: 'list',
  form: 'form'
} as const;

export const MENU_ROUTES = {
  myDashboard: 'my-dashboard',
  meteringData: 'metering-data',
  dataProcessing: 'data-processing',
  priceCalculation: 'price-calculation',
  nonCommodity: 'non-commodity',
  scenarioBuilder: 'scenario-builder',
  pricing: 'pricing',
  portfolioManagement: 'portfolio-management',
  accountAccess: 'account-access'
} as const;

export const ACCOUNTS_ACCESS_ROUTES = {
  companies: 'companies',
  groups: 'groups',
  users: 'users'
} as const;

export const PORTFOLIO_MANAGEMENT_ROUTES = {
  sites: 'sites',
  contracts: 'contracts',
  pod: 'metering-points'
} as const;

export const SYNTHETIC_PROFILE_ROUTES = {
  syntheticProfiles: 'synthetic-profiles',
  monthlyProfile: 'monthly-profile',
  hourlyProfile: 'hourly-profile',
  weeklyProfile: 'weekly-profile'
} as const;

export const HISTORICAL_PROFILE_ROUTES = {
  historicalProfiles: 'historical-profiles',
  monthlyProfile: 'monthly-profile',
  hourlyProfile: 'hourly-profile',
  weeklyProfile: 'weekly-profile'
} as const;

export const FORECASTED_PROFILES_ROUTES = {
  forecastedProfiles: 'forecasted-profiles'
} as const;

export const SCENARIO_ROUTES = {
  scenario: 'scenario'
} as const;

export const TARIFF_SCHEDULE_ROUTES = {
  tariffSchedule: 'tariff-schedule',
  form: APP_ROUTES.form
} as const;

export const CURRENCY_RATES_ROUTES = {
  currencyRates: 'currency-rates'
} as const;

export const CONVERSION_TOOL_ROUTES = {
  conversionTool: 'conversion-tool',
  dashboard: APP_ROUTES.dashboard,
  create: APP_ROUTES.create,
  chart: APP_ROUTES.chart,
  result: 'result'
} as const;

export const HOURLY_COST_ROUTES = {
  hourlyCost: 'hourly-cost',
  dashboard: APP_ROUTES.dashboard,
  create: APP_ROUTES.create,
  edit: APP_ROUTES.edit,
  charts: 'charts',
  granular: 'granular'
} as const;

export const HOURLY_PRICE_FORECAST_ROUTES = {
  hourlyPriceForecast: 'hourly-price-forecast',
  list: APP_ROUTES.list,
  create: APP_ROUTES.create,
  edit: APP_ROUTES.edit,
  chart: APP_ROUTES.chart
} as const;

export const PPA_ROUTES = {
  ppa: 'ppa',
  list: APP_ROUTES.list,
  create: APP_ROUTES.create,
  edit: APP_ROUTES.edit,
  charts: 'charts'
} as const;

export const NON_COMMODITY_ROUTES = {
  report: 'report',
  nonCommodityProfiles: 'non-commodity-profiles',
  additionalCharges: 'additional-charges'
} as const;

export const SCENARIO_BUILDER_ROUTES = {
  scenarioBuilder: 'scenario-builder'
} as const;

export const SAVED_SCENARIO_ROUTES = {
  savedScenario: 'saved-scenario'
} as const;

export const PRICING_ROUTES = {
  create: APP_ROUTES.create,
  edit: APP_ROUTES.edit,
  offers: 'offers',
  signedOffers: 'signed-offers',
  company: 'company',
  historicalProfiles: 'historical-profiles',
  users: 'users',
  report: 'report',
  allOffers: 'all-offers'
} as const;

export const ADDITIONAL_FEES_ROUTES = {
  additionalFees: 'additional-fees',
  report: 'report',
  create: APP_ROUTES.create,
  edit: APP_ROUTES.edit
} as const;

export const PRICING_TEMPLATES_ROUTES = {
  listTemplates: 'pricing-templates',
  create: APP_ROUTES.create,
  edit: APP_ROUTES.edit
} as const;

export const USER_ROUTES = {
  list: APP_ROUTES.list,
  internal: 'internal',
  external: 'external',
  createInternalUser: 'create-user',
  editInternalUser: 'edit-user',
  settingRoles: 'setting-roles',
  createCompany: 'create-company',
  editCompany: 'edit-company',
  externalUser: 'external-user',
  companyDetails: 'company-details'
} as const;

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [loginGuard],
    component: HomePageComponent
  },
  {
    path: APP_ROUTES.login,
    canActivate: [loginGuard],
    component: LoginComponent
  },
  { path: APP_ROUTES.resetPassword, component: ResetPasswordComponent },
  { path: APP_ROUTES.emailSent, component: EmailSentComponent },
  { path: APP_ROUTES.newPassword, component: NewPasswordComponent },
  {
    path: APP_ROUTES.componentsOverview,
    loadComponent: () =>
      import('./shared/components/components-overview/components-overview.component').then(
        c => c.ComponentsOverviewComponent
      )
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: APP_ROUTES.account,
        component: AccountComponent
      },
      {
        path: MENU_ROUTES.myDashboard,
        loadChildren: () => import('./core/routing/my-dashboard-routes').then(r => r.myDashboardRoutes)
      },
      {
        path: MENU_ROUTES.meteringData,
        children: [
          {
            path: SYNTHETIC_PROFILE_ROUTES.syntheticProfiles,
            loadChildren: () =>
              import('./modules/synthetic-profiles/synthetic-profile-routes').then(r => r.syntheticProfileRoutes)
          },
          {
            path: HISTORICAL_PROFILE_ROUTES.historicalProfiles,
            loadChildren: () =>
              import('./modules/historical-profiles/historical-profile-routes').then(r => r.historicalProfileRoutes)
          },
          {
            path: FORECASTED_PROFILES_ROUTES.forecastedProfiles,
            loadChildren: () =>
              import('./modules/forecasted-profiles/forecasted-profiles.routes').then(r => r.forecastedProfilesRoutes)
          },
          {
            path: '**',
            redirectTo: SYNTHETIC_PROFILE_ROUTES.syntheticProfiles
          }
        ]
      },
      {
        path: MENU_ROUTES.dataProcessing,
        children: [
          {
            path: TARIFF_SCHEDULE_ROUTES.tariffSchedule,
            loadChildren: () =>
              import('./modules/tariff-schedule/tariff-schedule-routes').then(r => r.tariffScheduleRoutes)
          },
          {
            path: CONVERSION_TOOL_ROUTES.conversionTool,
            loadChildren: () =>
              import('./modules/conversion-tool/conversion-tool-routes').then(r => r.conversionToolRoutes)
          },
          {
            path: CURRENCY_RATES_ROUTES.currencyRates,
            loadChildren: () =>
              import('./modules/currency-rates/currency-rates-routes').then(r => r.currencyRatesRoutes)
          },
          {
            path: '**',
            redirectTo: TARIFF_SCHEDULE_ROUTES.tariffSchedule
          }
        ]
      },
      {
        path: MENU_ROUTES.priceCalculation,
        children: [
          {
            path: HOURLY_COST_ROUTES.hourlyCost,
            loadChildren: () => import('./modules/hourly-cost/hourly-cost-routes').then(r => r.hourlyCostRoutes)
          },
          {
            path: HOURLY_PRICE_FORECAST_ROUTES.hourlyPriceForecast,
            loadChildren: () =>
              import('./modules/hourly-price-forecast/hourly-price-forecast-routes').then(
                r => r.hourlyPriceForecastRoutes
              )
          },
          {
            path: PPA_ROUTES.ppa,
            loadChildren: () => import('./modules/ppa/ppa-routes').then(r => r.ppaRoutes)
          },
          {
            path: '**',
            redirectTo: HOURLY_COST_ROUTES.hourlyCost
          }
        ]
      },
      {
        path: MENU_ROUTES.nonCommodity,
        children: [
          {
            path: NON_COMMODITY_ROUTES.nonCommodityProfiles,
            loadChildren: () =>
              import('./modules/non-commodity-profiles/non-commodity-profiles.routes').then(
                r => r.nonCommodityProfilesRoutes
              )
          },
          {
            path: NON_COMMODITY_ROUTES.additionalCharges,
            loadChildren: () =>
              import('./modules/additional-charges/additional-charges.routes').then(r => r.additionalChargesRoutes)
          },
          {
            path: '**',
            redirectTo: NON_COMMODITY_ROUTES.nonCommodityProfiles
          }
        ]
      },
      {
        path: MENU_ROUTES.scenarioBuilder,
        loadChildren: () => import('./modules/formula/formula-routes').then(r => r.formulaRoutes)
      },
      {
        path: MENU_ROUTES.pricing,
        children: [
          {
            path: PRICING_ROUTES.allOffers,
            loadChildren: () => import('./core/routing/offers-routes').then(r => r.offersRoutes)
          },
          {
            path: ADDITIONAL_FEES_ROUTES.additionalFees,
            loadChildren: () => import('./core/routing/additional-fees-routes').then(r => r.additonalFeesRoutes)
          },
          {
            path: PRICING_TEMPLATES_ROUTES.listTemplates,
            loadChildren: () => import('./core/routing/pricing-templates-routes').then(r => r.pricingTemplatesRoutes)
          },
          {
            path: '**',
            redirectTo: PRICING_ROUTES.allOffers
          }
        ]
      },
      {
        path: MENU_ROUTES.portfolioManagement,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: PORTFOLIO_MANAGEMENT_ROUTES.sites
          },
          {
            path: PORTFOLIO_MANAGEMENT_ROUTES.sites,
            loadChildren: () => import('./modules/site/site.routes').then(r => r.siteRoutes)
          },
          {
            path: PORTFOLIO_MANAGEMENT_ROUTES.pod,
            loadChildren: () => import('./modules/pod/pod.routes').then(r => r.podRoutes)
          },
          {
            path: PORTFOLIO_MANAGEMENT_ROUTES.contracts,
            loadChildren: () => import('./modules/contract/contract.routes').then(r => r.contractRoutes)
          },
          {
            path: '**',
            redirectTo: PORTFOLIO_MANAGEMENT_ROUTES.sites
          }
        ]
      },
      {
        path: MENU_ROUTES.accountAccess,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: ACCOUNTS_ACCESS_ROUTES.companies
          },
          {
            path: ACCOUNTS_ACCESS_ROUTES.companies,
            loadChildren: () => import('./modules/company/company.routes').then(r => r.companyRoutes)
          },
          {
            path: ACCOUNTS_ACCESS_ROUTES.groups,
            loadChildren: () => import('./modules/group/group.routes').then(r => r.groupRoutes)
          },
          {
            path: ACCOUNTS_ACCESS_ROUTES.users,
            loadChildren: () => import('./modules/user/user-routes').then(r => r.userRoutes)
          },
          {
            path: '**',
            redirectTo: ACCOUNTS_ACCESS_ROUTES.companies
          }
        ]
      },
      {
        path: '**',
        redirectTo: '/'
      }
    ]
  }
];
